<template>
  <div class="simulator-container">
    <!-- Панель инструментов -->
    <div class="toolbar">
      <button @click="addComponent('battery')">🔋 Батарея</button>
      <button @click="addComponent('led')">💡 Светодиод</button>
      <button @click="addComponent('resistor')">📏 Резистор</button>
      <button @click="addComponent('switch')">🔘 Выключатель</button>
      <button 
        @click="deleteSelected"
        :disabled="!selectedId"
      >
        🗑️ Удалить
      </button>
    </div>

    <!-- Рабочая область -->
    <div class="workspace" @click="clearSelection" ref="workspace">
      <!-- Компоненты -->
      <div
        v-for="comp in components"
        :key="comp.id"
        class="circuit-component"
        :class="[`type-${comp.type}`, { 
          selected: selectedId === comp.id, 
          powered: isPowered(comp), 
          dragging: isDragging && dragComp?.id === comp.id 
        }]"
        :style="getComponentStyle(comp)"
        @mousedown="startDrag(comp, $event)"
        @click.stop="selectComponent(comp)"
      >
        <!-- Точки соединения -->
        <div
          v-for="port in getPorts(comp)"
          :key="port.id"
          class="port"
          :class="[port.type, { connected: isPortConnected(comp.id, port.id) }]"
          :style="getPortStyle(comp, port)"
          @mousedown.stop="startConnection($event, comp, port)"
          @mouseup.stop="finishConnection($event, comp, port)"
          @mouseenter="hoveredPort = { componentId: comp.id, portId: port.id }"
          @mouseleave="hoveredPort = null"
        ></div>

        <!-- Отображение компонента -->
        <div class="component-icon">{{ componentIcons[comp.type] }}</div>
        <div class="component-label">{{ getLabel(comp) }}</div>

        <!-- Специфические элементы компонентов -->
        <div 
          v-if="comp.type === 'led'"
          class="led"
          :class="{ 'on': comp.isOn }"
          :style="{
            opacity: comp.brightness || 0,
            filter: `brightness(${(comp.brightness || 0) * 100}%)`
          }"
        >
          <div class="led-glow"></div>
        </div>

        <div 
          v-if="comp.type === 'switch'"
          class="switch"
          :class="{ 'on': comp.isOn }"
          @click.stop="toggleSwitch(comp)"
        >
          <div class="switch-handle"></div>
        </div>
      </div>

      <!-- Провода -->
      <svg class="wires" ref="wiresSvg">
        <path
          v-for="(wire, index) in wires"
          :key="index"
          :d="getWirePath(wire)"
          :class="{ 'active': isWireActive(wire) }"
          @click.stop="selectWire(wire)"
        />
        <!-- Временный провод -->
        <path
          v-if="tempWire"
          :d="getTempWirePath()"
          class="temp"
        />
      </svg>
    </div>

    <!-- Кнопка включения -->
    <button 
      v-if="isCircuitClosed"
      @click="togglePower"
      :class="{ 'active': isPowerOn }"
      class="power-button"
    >
      {{ isPowerOn ? 'Выключить' : 'Включить' }}
    </button>
  </div>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue'

// Состояние симулятора
const components = ref([])
const wires = ref([])
const tempWire = ref(null)
const isCircuitClosed = ref(false)
const isPowerOn = ref(false)
const selectedId = ref(null)
const hoveredPort = ref(null)

// Состояние перетаскивания
const isDragging = ref(false)
const dragComp = ref(null)
const dragOffset = ref({ x: 0, y: 0 })
const workspaceRef = ref(null)

// Иконки компонентов
const componentIcons = {
  battery: '🔋',
  led: '💡',
  switch: '🔘',
  resistor: '📏'
}

// Методы для работы с компонентами
const addComponent = (type) => {
  const baseComponent = {
    id: Date.now().toString(),
    type,
    x: 100,
    y: 100,
    rotation: 0
  }
  
  const specificProps = {
    battery: { voltage: 1.5 },
    led: { color: 'red', isOn: false, brightness: 0 },
    resistor: { resistance: 100 },
    switch: { isOn: false }
  }
  
  components.value.push({
    ...baseComponent,
    ...specificProps[type]
  })
}

const deleteSelected = () => {
  if (!selectedId.value) return
  
  // Удаляем компонент
  components.value = components.value.filter(c => c.id !== selectedId.value)
  
  // Удаляем связанные провода
  wires.value = wires.value.filter(w => 
    w.from.id !== selectedId.value && w.to.id !== selectedId.value
  )
  
  selectedId.value = null
  checkCircuit()
}

const selectComponent = (comp) => {
  selectedId.value = comp.id
}

const clearSelection = () => {
  selectedId.value = null
}

const getLabel = (comp) => {
  const labels = {
    battery: 'Батарея',
    led: 'Светодиод',
    resistor: 'Резистор',
    switch: 'Выключатель'
  }
  return labels[comp.type] || comp.type
}

// Методы для работы с проводами
const getPorts = (comp) => {
  const portDefinitions = {
    battery: [
      { id: 'positive', type: 'output', x: 0.5, y: 0 },
      { id: 'negative', type: 'input', x: 0.5, y: 1 }
    ],
    led: [
      { id: 'anode', type: 'input', x: 0, y: 0.5 },
      { id: 'cathode', type: 'output', x: 1, y: 0.5 }
    ],
    resistor: [
      { id: 'a', type: 'bidirectional', x: 0, y: 0.5 },
      { id: 'b', type: 'bidirectional', x: 1, y: 0.5 }
    ],
    switch: [
      { id: 'in', type: 'input', x: 0, y: 0.5 },
      { id: 'out', type: 'output', x: 1, y: 0.5 }
    ]
  }
  return portDefinitions[comp.type] || []
}

const getPortStyle = (comp, port) => {
  const size = 12
  const width = 70
  const height = 50
  const x = port.x * width - size/2
  const y = port.y * height - size/2
  
  // Учет вращения
  const centerX = width / 2
  const centerY = height / 2
  const rad = ((comp.rotation || 0) * Math.PI) / 180
  
  const rotatedX = centerX + (x + size/2 - centerX) * Math.cos(rad) - (y + size/2 - centerY) * Math.sin(rad)
  const rotatedY = centerY + (x + size/2 - centerX) * Math.sin(rad) + (y + size/2 - centerY) * Math.cos(rad)
  
  return {
    left: `${rotatedX - size/2}px`,
    top: `${rotatedY - size/2}px`
  }
}

const startConnection = (event, comp, port) => {
  event.stopPropagation()
  selectedId.value = comp.id
  
  if (!workspaceRef.value) return
  
  const rect = workspaceRef.value.getBoundingClientRect()
  const portPos = getAbsolutePortPosition(comp, port)
  
  tempWire.value = {
    from: { id: comp.id, port: port.id },
    start: { x: portPos.x, y: portPos.y },
    end: { x: event.clientX - rect.left, y: event.clientY - rect.top }
  }
  
  document.addEventListener('mousemove', handleConnectionMove)
  document.addEventListener('mouseup', finishConnection)
}

const handleConnectionMove = (event) => {
  if (!tempWire.value || !workspaceRef.value) return
  
  const rect = workspaceRef.value.getBoundingClientRect()
  tempWire.value.end = {
    x: event.clientX - rect.left,
    y: event.clientY - rect.top
  }
}

const finishConnection = (event, comp, port) => {
  if (!tempWire.value) {
    cleanupConnectionListeners()
    return
  }
  
  // Проверяем, что соединяем разные компоненты
  if (tempWire.value.from.id === comp.id) {
    tempWire.value = null
    cleanupConnectionListeners()
    return
  }
  
  // Создаем новый провод
  const newWire = {
    from: tempWire.value.from,
    to: { id: comp.id, port: port.id },
    start: tempWire.value.start,
    end: getAbsolutePortPosition(comp, port)
  }
  
  wires.value.push(newWire)
  tempWire.value = null
  cleanupConnectionListeners()
  checkCircuit()
}

const cleanupConnectionListeners = () => {
  document.removeEventListener('mousemove', handleConnectionMove)
  document.removeEventListener('mouseup', finishConnection)
}

const getAbsolutePortPosition = (comp, port) => {
  const width = 70
  const height = 50
  const x = comp.x + port.x * width
  const y = comp.y + port.y * height
  
  // Учет вращения
  const centerX = comp.x + width / 2
  const centerY = comp.y + height / 2
  const rad = ((comp.rotation || 0) * Math.PI) / 180
  
  const rotatedX = centerX + (x - centerX) * Math.cos(rad) - (y - centerY) * Math.sin(rad)
  const rotatedY = centerY + (x - centerX) * Math.sin(rad) + (y - centerY) * Math.cos(rad)
  
  return { x: rotatedX, y: rotatedY }
}

const getWirePath = (wire) => {
  if (!wire || !wire.start || !wire.end) return ''
  return `M${wire.start.x},${wire.start.y} L${wire.end.x},${wire.end.y}`
}

const getTempWirePath = () => {
  if (!tempWire.value) return ''
  return getWirePath(tempWire.value)
}

const isPortConnected = (componentId, portId) => {
  return wires.value.some(w => 
    (w.from.id === componentId && w.from.port === portId) ||
    (w.to.id === componentId && w.to.port === portId)
  )
}

const selectWire = (wire) => {
  selectedId.value = `wire-${wires.value.indexOf(wire)}`
}

// Методы для перетаскивания
const startDrag = (comp, event) => {
  if (event.button !== 0) return // Только левая кнопка мыши
  
  if (!workspaceRef.value) return
  
  isDragging.value = true
  dragComp.value = comp
  selectedId.value = comp.id
  
  const rect = workspaceRef.value.getBoundingClientRect()
  dragOffset.value = {
    x: event.clientX - rect.left - comp.x,
    y: event.clientY - rect.top - comp.y
  }
  
  event.preventDefault()
  event.stopPropagation()
  
  document.addEventListener('mousemove', handleDrag)
  document.addEventListener('mouseup', stopDrag)
}

const handleDrag = (event) => {
  if (!isDragging.value || !dragComp.value) return
  
  const rect = workspaceRef.value.getBoundingClientRect()
  const newX = event.clientX - rect.left - dragOffset.value.x
  const newY = event.clientY - rect.top - dragOffset.value.y
  
  // Обновляем позицию компонента
  const index = components.value.findIndex(c => c.id === dragComp.value.id)
  if (index !== -1) {
    components.value[index].x = Math.max(0, newX)
    components.value[index].y = Math.max(0, newY)
  }
  
  // Обновляем позиции проводов
  updateWirePositions()
}

const stopDrag = () => {
  isDragging.value = false
  dragComp.value = null
  document.removeEventListener('mousemove', handleDrag)
  document.removeEventListener('mouseup', stopDrag)
  checkCircuit()
}

// Обновляем стили компонента
const getComponentStyle = (comp) => {
  return {
    left: `${comp.x}px`,
    top: `${comp.y}px`,
    transform: `rotate(${comp.rotation || 0}deg)`,
    '--component-color': comp.color || '#ff0000',
    cursor: isDragging.value && dragComp.value?.id === comp.id ? 'grabbing' : 'grab',
    position: 'absolute',
    userSelect: 'none'
  }
}

const updateWirePositions = () => {
  wires.value.forEach(wire => {
    const fromComp = components.value.find(c => c.id === wire.from.id)
    const toComp = components.value.find(c => c.id === wire.to.id)
    
    if (fromComp) {
      const fromPort = getPorts(fromComp).find(p => p.id === wire.from.port)
      if (fromPort) {
        wire.start = getAbsolutePortPosition(fromComp, fromPort)
      }
    }
    
    if (toComp) {
      const toPort = getPorts(toComp).find(p => p.id === wire.to.port)
      if (toPort) {
        wire.end = getAbsolutePortPosition(toComp, toPort)
      }
    }
  })
}

// Логика работы цепи
const checkCircuit = () => {
  const powerSource = components.value.find(c => c.type === 'battery')
  const led = components.value.find(c => c.type === 'led')
  
  if (!powerSource || !led) {
    isCircuitClosed.value = false
    return
  }

  const hasPath = checkPath(powerSource, led)
  isCircuitClosed.value = hasPath
}

const checkPath = (start, end, visited = new Set()) => {
  if (start.id === end.id) return true
  if (visited.has(start.id)) return false
  
  visited.add(start.id)
  
  const connectedWires = wires.value.filter(w => 
    w.from.id === start.id || w.to.id === start.id
  )
  
  for (const wire of connectedWires) {
    const nextCompId = wire.from.id === start.id ? wire.to.id : wire.from.id
    const nextComp = components.value.find(c => c.id === nextCompId)
    
    if (nextComp && checkPath(nextComp, end, visited)) {
      return true
    }
  }
  
  return false
}

const togglePower = () => {
  if (!isCircuitClosed.value) return
  
  isPowerOn.value = !isPowerOn.value
  updateComponentsState()
}

const updateComponentsState = () => {
  components.value.forEach(comp => {
    if (comp.type === 'led') {
      comp.isOn = isPowerOn.value && isCircuitClosed.value
      comp.brightness = comp.isOn ? 1 : 0
    }
  })
}

const toggleSwitch = (comp) => {
  if (comp.type !== 'switch') return
  
  comp.isOn = !comp.isOn
  checkCircuit()
  updateComponentsState()
}

const isPowered = (comp) => {
  if (!isPowerOn.value || !isCircuitClosed.value) return false
  
  if (comp.type === 'led') {
    return comp.isOn
  }
  
  return false
}

const isWireActive = () => {
  return isPowerOn.value && isCircuitClosed.value
}

// Наблюдатели
watch(wires, () => {
  checkCircuit()
  updateComponentsState()
}, { deep: true })

watch(components, () => {
  updateWirePositions()
}, { deep: true })

watch(isPowerOn, () => {
  updateComponentsState()
})

onMounted(() => {
  // Инициализация workspaceRef после монтирования
  workspaceRef.value = document.querySelector('.workspace')
})
</script>

<style scoped>
.simulator-container {
  position: relative;
  width: 100%;
  height: 100vh;
  overflow: hidden;
  background-color: #f5f5f5;
}

.toolbar {
  position: absolute;
  top: 10px;
  left: 10px;
  display: flex;
  gap: 8px;
  background: white;
  padding: 8px;
  border-radius: 4px;
  box-shadow: 0 2px 5px rgba(0,0,0,0.1);
  z-index: 100;
}

.toolbar button {
  padding: 6px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: #f5f5f5;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
}

.toolbar button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.workspace {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: 
    linear-gradient(#e0e0e0 1px, transparent 1px),
    linear-gradient(90deg, #e0e0e0 1px, transparent 1px);
  background-size: 20px 20px;
}

.circuit-component {
  position: absolute;
  width: 70px;
  height: 50px;
  border: 2px solid #aaa;
  border-radius: 8px;
  background: #f5f5f5;
  cursor: grab;
  user-select: none;
  transition: transform 0.1s ease, box-shadow 0.1s ease, border-color 0.2s ease;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 1;
}

.circuit-component.selected {
  border-color: #4285f4;
  box-shadow: 0 0 0 2px rgba(66, 133, 244, 0.3);
  z-index: 100;
}

.circuit-component.powered {
  border-color: #f4b400;
  animation: pulse-border 1s infinite alternate;
}

.circuit-component.dragging {
  z-index: 1000;
  opacity: 0.9;
  cursor: grabbing;
}

.circuit-component.type-led {
  background: #333;
}

.circuit-component.type-battery {
  background: #fff9c4;
}

.circuit-component.type-resistor {
  background: #e1f5fe;
}

.circuit-component.type-switch {
  background: #ffffff;
}

@keyframes pulse-border {
  from { border-color: #f4b400; }
  to { border-color: #ffca28; }
}

.component-icon {
  font-size: 20px;
  margin-bottom: 4px;
  pointer-events: none;
}

.component-label {
  font-size: 12px;
  font-weight: bold;
  color: #333;
  pointer-events: none;
  margin-top: 4px;
}

.circuit-component.type-led .component-label {
  color: #fff;
}

.port {
  position: absolute;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #2196F3;
  cursor: crosshair;
  z-index: 2;
  transition: all 0.2s ease;
}

.port:hover {
  transform: scale(1.2);
  background: #1976D2;
}

.port.connected {
  background: #4CAF50;
}

.port.input {
  background: #4CAF50;
}

.port.output {
  background: #F44336;
}

.wires {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;
}

.wires path {
  stroke: #555;
  stroke-width: 2;
  fill: none;
}

.wires path.active {
  stroke: #4CAF50;
  stroke-width: 3;
}

.wires path.temp {
  stroke: #999;
  stroke-dasharray: 5;
  stroke-width: 2;
}

.led {
  position: relative;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #333;
  transition: all 0.3s ease;
}

.led.on {
  background: var(--component-color, #ff0000);
  box-shadow: 0 0 10px var(--component-color, #ff0000);
}

.led-glow {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 50%;
  background: inherit;
  filter: blur(5px);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.led.on .led-glow {
  opacity: 0.5;
}

.switch {
  position: relative;
  width: 40px;
  height: 20px;
  background: #ccc;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.3s ease; 
}

.switch.on {
  background: #4CAF50;
}

.switch-handle {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 16px;
  height: 16px;
  background: white;
  border-radius: 50%;
  transition: transform 0.3s ease;
}

.switch.on .switch-handle {
  transform: translateX(20px);
}

.power-button {
  position: absolute;
  bottom: 20px;
  right: 20px;
  padding: 10px 20px;
  background: #f5f5f5;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  z-index: 100;
}

.power-button.active {
  background-color: #4CAF50;
  color: white;
}

.power-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>